
import { defineComponent, ref, watch, reactive, toRefs, PropType, nextTick } from 'vue'
import * as XLSX from 'xlsx'
import Papa from 'papaparse'
import slug from 'slug'

import _ from 'lodash'

import { isFileType } from '@/utils/file'

function capabilities () {
  const e = document.createElement('div')
  return {
    drop: (('draggable' in e) || ('ondragstart' in e && 'ondrop' in e)),
    reader: 'FormData' in window && 'FileReader' in window
  }
}

export default defineComponent({
  name: 'FileDrop',
  props: {
    id: {
      type: String,
      default: Date.now().toString(32)
    },
    file: {
      type: File,
      default: null
    },
    inputChange: {
      type: Function,
      default: (e: any) => {
        console.log(e)
      }
    },
    label: {
      type: String,
      default: 'Drop a file '
    },
    accepts: {
      type: Array as PropType<Array<string>>,
      default: () => ['.csv', '.xlsx', '.xls']
    },
    max: {
      type: Number,
      default: 1
    },
    isImage: {
      type: Boolean,
      default: false
    },
    preParse: Function,
    postParse: Function
  },
  emits: ['change', 'drop'],
  setup (props, { emit }) {
    const state = reactive({
      loading: false,
      importDone: false,
      importData: {
        raw: null,
        workbook: null,
        worksheet: null
      },
      csvData: {
        header: null,
        results: null,
        csvString: null
      },
      productImageFile: ref(),
      fileList: ref([] as any[]),
      fileInput: ref()
    })

    watch(() => state.fileList, () => {
      emit('change', { files: state.fileList })
    }, { deep: true })

    watch(() => state.productImageFile, () => {
      emit('change', state.productImageFile)
    })

    async function previewImage () {
      await nextTick()
      if (state?.fileList[0]) {
        const el = document.querySelector('.productImage')
        if (el) {
          el?.setAttribute('src', URL.createObjectURL(state.fileList[0]))
        }
      }
    }

    function onChange (event) {
      const files = event.target.files
      const rawFile = files[0] // Only use the first one, because we are accepting only one file
      if (!rawFile) return

      // Input handles checks itself so we can skip those

      assert(event)
    }

    function assert (event) {
      const files: Array<File> = event.type === 'change' ? event.srcElement.files : event.dataTransfer.files
      if (files.length > props.max) {
        console.error(`Only supports uploading of ${props.max} files!`)
        if (!event.currentTarget.classList.contains('file-drop--error')) {
          event.currentTarget.classList.add('file-drop--error')
        }
        return
      }

      // console.log('Files list: ', files)
      const rawFile = files[0] // Only use the first one, because we are accepting only one file
      if (!isFileType(rawFile, props.accepts)) {
        console.error(`Only supports upload for ${props.accepts}`)
        if (!event.currentTarget.classList.contains('file-drop--error')) {
          event.currentTarget.classList.add('file-drop--error')
        }
        return false
      }

      parse(rawFile)
    }


    function remove (index?) {
      state.fileList = []
      state.csvData = { header: null, results: null, csvString: null }
      state.importDone = false
      props.postParse && props.postParse(state.csvData)
    }

    function commitData ({ raw, workbook, worksheet, header, results, csvString }) {
      state.importData = { raw, workbook, worksheet }
      state.csvData = { header, results, csvString }

      props.postParse && props.postParse(state.csvData)
    }

    function parse (file: File) {
      state.fileList[0] = file

      if (!props.preParse) {
        parseFile(file)
        return
      }

      if (props.preParse(file)) {
        parseFile(file)
      }
    }

    function updateSheetRange (ws: XLSX.WorkSheet): string {
      const range = { s: { r: Infinity, c: Infinity }, e: { r: 0, c: 0 } }
      Object.keys(ws)
        .filter(x => x.charAt(0) != '!')
        .map(XLSX.utils.decode_cell)
        .forEach(function (x) {
          range.s.c = Math.min(range.s.c, x.c)
          range.s.r = Math.min(range.s.r, x.r)
          range.e.c = Math.max(range.e.c, x.c)
          range.e.r = Math.max(range.e.r, x.r)
        })
      return XLSX.utils.encode_range(range)
    }

    function parseFile (file: File) {
      state.loading = true

      // return new Promise((resolve, reject) => {
      let csvString: any
      let results: any

      if (file.type === 'text/csv') {
        Papa.parse(file, {
          // worker: true,
          complete: ({ data }) => {
            const [header, ...rows] = data

            const mappedHead = (header as Array<string>)?.map((d, i) => _.kebabCase(d)) // sluggifies all header row cells
            csvString = Papa.unparse([mappedHead, ...rows], { delimiter: ',' })
            results = rows.map((row: any) => {
              return _.zipObject(mappedHead, row)
            })

            // update state with new data
            commitData({ raw: null, workbook: null, worksheet: null, header: mappedHead, results, csvString })
            state.loading = false
          }
        })
        return
      }

      if (['image/jpeg', 'image/png', 'image/svg+xml'].includes(file.type)) {
        const reader = new FileReader()
        reader.readAsDataURL(file)

        reader.onloadend = function () {
          state.productImageFile = { fileName: file.name, file: reader.result as string }
          console.log(state)
          void previewImage()
        }

        reader.onerror = e => {
          console.error(e)
        }
        return
      }

      const reader = new FileReader()
      reader.onload = e => {
        const raw = e.target?.result
        // console.log('reader onload', raw)
        const workbook = XLSX.read(raw, { type: 'array', raw: true })
        // console.log(workbook)
        const worksheet = workbook.Sheets[workbook.SheetNames[0]]
        worksheet['!ref'] = updateSheetRange(worksheet)
        let header: any[] = getHeaderRow(worksheet)
        // console.log('header', header)
        // fix for all-number ids exceeding the js max_safe_int threshold
        // when xlsx is imported, numbers are preserved, and if used as ids they can exceed the js safe limit, \
        // causing scientific "E+" notation when converted to strings
        // so we have to do this before doing anything else
        let modRows: any[] = []
        Object.keys(worksheet).forEach((row, i) => {
          // check for Id name
          // fix rich text encoding
          // if(typeof worksheet[row].r == 'string') {
          //   if(worksheet[row].r.indexOf('<x:t>') != -1) {
          //     worksheet[row].r = worksheet[row].r.replace('<x:t>', '<t>')
          //     worksheet[row].r = worksheet[row].r.replace('</x:t>', '</t>')
          //   }
          // }

          if (row.indexOf('!') >= 0 || !worksheet[row].v) return
          if (['id', 'ref', 'reference', 'phone'].includes(worksheet[row].v.toString().toLowerCase())) {
            modRows.push(row)
          }
        })
        // console.log('worksheet', worksheet)
        // trim number from row keys
        modRows = modRows.map(r => r.replace(/[0-9]/g, ''))

        Object.keys(worksheet).forEach((row, i) => {
          if (modRows.includes(row.replace(/[0-9]/g, ''))) {
            worksheet[row].w = worksheet[row].v.toString()
          }
        })
        csvString = XLSX.utils.sheet_to_csv(worksheet, { blankrows: false })
        results = XLSX.utils.sheet_to_json(worksheet, { blankrows: false })

        // normalize header data
        header = header.map((item, i) => {
          csvString = csvString.replace(item, _.kebabCase(item))
          return _.kebabCase(item)
        })

        // console.log(csvString, results)

        // update state with new data
        commitData({ raw, workbook, worksheet, header, results, csvString })

        state.loading = false
      }
      // console.log('RAWFILE', reader.readAsArrayBuffer(file).toString('utf8'))
      reader.readAsArrayBuffer(file)
    }

    function getHeaderRow (sheet) {
      const headers: any[] = []
      const range = XLSX.utils.decode_range(sheet['!ref'])
      let C
      const R = range.s.r
      /* start in the first row */
      for (C = range.s.c; C <= range.e.c; ++C) { /* walk every column in the range */
        const cell = sheet[XLSX.utils.encode_cell({ c: C, r: R })]
        /* find the cell in the first row */
        let hdr = `UNKNOWN ${C}` // <-- replace with your desired default
        if (cell && cell.t) hdr = XLSX.utils.format_cell(cell)
        headers.push(hdr)
      }
      return headers
    }

    function dragover (event) {
      event.preventDefault()
      if (event.currentTarget.classList.contains('file-drop--error')) event.currentTarget.classList.remove('file-drop--error')
      if (!event.currentTarget.classList.contains('file-drop--hover')) event.currentTarget.classList.add('file-drop--hover')
    }

    function dragleave (event) {
      event.currentTarget.classList.remove('file-drop--hover')
    }

    function drop (event) {
      event.stopPropagation()
      event.preventDefault()

      assert(event)
    }

    return {
      ...toRefs(state),
      onChange,
      remove,
      dragover,
      dragleave,
      drop
    }
  }
})
